import { CustomPreference } from '../utils/api/CustomPreference';

export enum RecaptchaAction {
    LOGIN = 'login',
    LOGIN_CHECKOUT = 'login_checkout',
    REGISTER = 'register',
    REGISTER_ORDER_CONFIRMATION = 'register_order_confirmation',
    /* TODO: Uncomment when recaptcha is implemented in these features
    FORGOT_PASSWORD = 'forgot_password',
    RESET_PASSWORD = 'reset_password',
    TRACK_ORDER = 'track_order',
    */
    GIFT_CARD_BALANCE_LANDING_PAGE = 'gc_balance_landing_page',
    GIFT_CARD_BALANCE_DETAIL_PAGE = 'gc_balance_detail_page',
    PROMO_CODE_CART = 'promo_code_cart',
    PROMO_CODE_CHECKOUT = 'promo_code_checkout',
    /* TODO: Uncomment when recaptcha is implemented in these features
    ADD_PAYMENT_METHOD = 'add_payment_method',
    PLACE_ORDER = 'place_order',
    */
}

export enum RecaptchaFeature {
    ACCOUNT_LOGIN = 'accountLogin',
    ACCOUNT_REGISTRATION = 'accountRegistration',
    /* TODO: Uncomment when recaptcha is implemented in these features
    FORGOT_PASSWORD = 'forgotPassword',
    PASSWORD_RESET = 'passwordReset',
    TRACK_ORDER = 'trackOrder',
    */
    GIFT_CARD_BALANCE = 'giftCardBalance',
    PROMO_CODE = 'promoCode',
    /* TODO: Uncomment when recaptcha is implemented in these features
    ADD_PAYMENT_METHOD = 'addPaymentMethod',
    PLACE_ORDER = 'placeOrder',
    */
}

export enum RecaptchaVersion {
    V2 = 'v2',
    V3 = 'v3',
}

export interface RecaptchaPayload {
    token: string;
    version: RecaptchaVersion;
    action: RecaptchaAction;
    feature: RecaptchaFeature;
    assessmentName?: string;
    aggregatedEventToken?: string;
}

export interface RecaptchaFeatureConfig {
    enabled: boolean;
    enableV2Fallback: boolean;
}

export interface RecaptchaConfig {
    [RecaptchaFeature.ACCOUNT_LOGIN]: RecaptchaFeatureConfig;
    [RecaptchaFeature.ACCOUNT_REGISTRATION]: RecaptchaFeatureConfig;
    /* TODO: Uncomment when recaptcha is implemented in these features
    [RecaptchaFeature.FORGOT_PASSWORD]: RecaptchaFeatureConfig;
    [RecaptchaFeature.PASSWORD_RESET]: RecaptchaFeatureConfig;
    [RecaptchaFeature.TRACK_ORDER]: RecaptchaFeatureConfig;
    */
    [RecaptchaFeature.GIFT_CARD_BALANCE]: RecaptchaFeatureConfig;
    [RecaptchaFeature.PROMO_CODE]: RecaptchaFeatureConfig;
    /* TODO: Uncomment when recaptcha is implemented in these features
    [RecaptchaFeature.ADD_PAYMENT_METHOD]: RecaptchaFeatureConfig;
    [RecaptchaFeature.PLACE_ORDER]: RecaptchaFeatureConfig;
    */
}

export interface RecaptchaError {
    code: string;
    message: string;
    assessmentName?: string;
}

export interface RecaptchaEnterpriseConfig {
    projectId: string;
    apiKey: string;
    siteKeyV3: string;
    siteKeyV2: string;
}

// All possible reCAPTCHA error codes from backend
export enum RecaptchaErrorCode {
    ACTION_MISMATCH = 'RECAPTCHA_ACTION_MISMATCH',
    UNSUPPORTED_VERSION = 'RECAPTCHA_UNSUPPORTED_VERSION',
    INVALID_TOKEN = 'RECAPTCHA_INVALID_TOKEN',
    TOKEN_EXPIRED = 'RECAPTCHA_TOKEN_EXPIRED',
    TOKEN_ACTION_MISMATCH = 'RECAPTCHA_TOKEN_ACTION_MISMATCH',
    HOSTNAME_MISMATCH = 'RECAPTCHA_HOSTNAME_MISMATCH',
    V2_REQUIRED = 'RECAPTCHA_V2_REQUIRED',
    V2_DISABLED = 'RECAPTCHA_V2_DISABLED',
    VERIFICATION_FAILED = 'RECAPTCHA_VERIFICATION_FAILED',
}

export interface RecaptchaErrorResponse {
    statusCode: 400;
    message: string;
    errorCode: RecaptchaErrorCode;
    feature: string;
}

export interface FeaturePreferenceEntry {
    enabledPref: CustomPreference;
    v2FallbackPref: CustomPreference;
}

export const FEATURE_PREFERENCE_MAP: Record<RecaptchaFeature, FeaturePreferenceEntry> = {
    [RecaptchaFeature.ACCOUNT_LOGIN]: {
        enabledPref: CustomPreference.RECAPTCHA_ACCOUNT_LOGIN_ENABLED,
        v2FallbackPref: CustomPreference.RECAPTCHA_ACCOUNT_LOGIN_ENABLE_V2_FALLBACK,
    },
    [RecaptchaFeature.ACCOUNT_REGISTRATION]: {
        enabledPref: CustomPreference.RECAPTCHA_ACCOUNT_REGISTRATION_ENABLED,
        v2FallbackPref: CustomPreference.RECAPTCHA_ACCOUNT_REGISTRATION_ENABLE_V2_FALLBACK,
    },
    /* TODO: Uncomment when recaptcha is implemented in these features
    [RecaptchaFeature.FORGOT_PASSWORD]: {
        enabledPref: CustomPreference.RECAPTCHA_FORGOT_PASSWORD_ENABLED,
        v2FallbackPref: CustomPreference.RECAPTCHA_FORGOT_PASSWORD_ENABLE_V2_FALLBACK,
    },
    [RecaptchaFeature.PASSWORD_RESET]: {
        enabledPref: CustomPreference.RECAPTCHA_PASSWORD_RESET_ENABLED,
        v2FallbackPref: CustomPreference.RECAPTCHA_PASSWORD_RESET_ENABLE_V2_FALLBACK,
    },
    [RecaptchaFeature.TRACK_ORDER]: {
        enabledPref: CustomPreference.RECAPTCHA_TRACK_ORDER_ENABLED,
        v2FallbackPref: CustomPreference.RECAPTCHA_TRACK_ORDER_ENABLE_V2_FALLBACK,
    },
    */
    [RecaptchaFeature.GIFT_CARD_BALANCE]: {
        enabledPref: CustomPreference.RECAPTCHA_GIFT_CARD_BALANCE_ENABLED,
        v2FallbackPref: CustomPreference.RECAPTCHA_GIFT_CARD_BALANCE_ENABLE_V2_FALLBACK,
    },
    [RecaptchaFeature.PROMO_CODE]: {
        enabledPref: CustomPreference.RECAPTCHA_PROMO_CODE_ENABLED,
        v2FallbackPref: CustomPreference.RECAPTCHA_PROMO_CODE_ENABLE_V2_FALLBACK,
    },
    /* TODO: Uncomment when recaptcha is implemented in these features
    [RecaptchaFeature.ADD_PAYMENT_METHOD]: {
        enabledPref: CustomPreference.RECAPTCHA_ADD_PAYMENT_METHOD_ENABLED,
        v2FallbackPref: CustomPreference.RECAPTCHA_ADD_PAYMENT_METHOD_ENABLE_V2_FALLBACK,
    },
    [RecaptchaFeature.PLACE_ORDER]: {
        enabledPref: CustomPreference.RECAPTCHA_PLACE_ORDER_ENABLED,
        v2FallbackPref: CustomPreference.RECAPTCHA_PLACE_ORDER_ENABLE_V2_FALLBACK,
    },
    */
};

export const RECAPTCHA_CUSTOM_PREFERENCES = [
    ...Object.values(FEATURE_PREFERENCE_MAP).flatMap(({ enabledPref, v2FallbackPref }) => [
        enabledPref,
        v2FallbackPref,
    ]),
    CustomPreference.RECAPTCHA_PROJECT_ID,
    CustomPreference.RECAPTCHA_API_KEY,
    CustomPreference.RECAPTCHA_V2_SITE_KEY,
    CustomPreference.RECAPTCHA_V3_SITE_KEY,
];
